import React, { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { LuShield } from "react-icons/lu";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { updatePassword } from "../../redux/user/userSlice";
import AcknowledgementModel from "../Modal/AcknowledgementModel";
import authService from "../../redux/auth/authService";

const DARK_NAVY = "#051525";
const DARK_NAVY_LIGHT = "#4a6278";
const BLUE = "#1E3A5F";
const PROFILE_FONT = '"Inter", sans-serif';
const BTN_HOVER = "#162C47";
const INPUT_BORDER = "#d0dff0";
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";
const INPUT_FOCUS_GLOW =
  "0 0 0 3px rgba(30, 58, 95, 0.12), 0 4px 14px rgba(30, 58, 95, 0.18)";

const resetPasswordValues = {
  currentPassword: "",
  password: "",
  confirmPassword: "",
};

const FormSchema = yup.object().shape({
  currentPassword: yup.string().required("Current password is required."),
  password: yup
    .string()
    .min(8, "New password must be at least 8 characters long.")
    .matches(/[0-9]/, "New password must contain at least one number.")
    .matches(/[a-z]/, "New password must contain at least one lowercase letter.")
    .matches(/[A-Z]/, "New password must contain at least one uppercase letter.")
    .matches(/[^\w]/, "New password must contain at least one special character.")
    .required("New password is required."),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), undefined], "Confirm password must match new password.")
    .required("Confirm password is required."),
});

const fieldLabels: Record<string, string> = {
  currentPassword: "Current Password",
  password: "New Password",
  confirmPassword: "Confirm New Password",
};

const PasswordField = ({
  name,
  label,
  showPassword,
  onToggle,
}: {
  name: string;
  label: string;
  showPassword: boolean;
  onToggle: () => void;
}) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography
      sx={{
        fontFamily: PROFILE_FONT,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: DARK_NAVY_LIGHT,
        mb: 1,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ position: "relative" }}>
      <Field
        name={name}
        type={showPassword ? "text" : "password"}
        style={{
          width: "100%",
          padding: "14px 48px 14px 16px",
          borderRadius: 10,
          border: `2px solid ${INPUT_BORDER}`,
          fontSize: 15,
          color: DARK_NAVY,
          outline: "none",
          boxSizing: "border-box",
          backgroundColor: "#ffffff",
        }}
        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
          e.target.style.borderColor = BLUE;
          e.target.style.boxShadow = INPUT_FOCUS_GLOW;
        }}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          e.target.style.borderColor = INPUT_BORDER;
          e.target.style.boxShadow = "none";
        }}
      />
      <IconButton
        onClick={onToggle}
        sx={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          color: DARK_NAVY_LIGHT,
        }}
        aria-label="toggle password visibility"
      >
        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </Box>
    <ErrorMessage name={name}>
      {(msg) => (
        <Typography sx={{ fontSize: 12, color: "#c0392b", mt: 0.5 }}>{msg}</Typography>
      )}
    </ErrorMessage>
  </Box>
);

const UpdatePassword = () => {
  const dispatch = useDispatch<any>();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showModel, setShowModel] = useState(false);

  const onConfirm = () => {
    authService.logout();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
      }}
    >
      <Box sx={{ maxWidth: 560, width: "100%" }}>
      <Box
        sx={{
          bgcolor: "#ffffff",
          borderRadius: "16px",
          boxShadow: CARD_SHADOW,
          border: "1px solid rgba(0,0,0,0.04)",
          p: { xs: 2.5, md: 3.5 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LuShield size={24} color="#ffffff" />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 18, fontWeight: 700, color: DARK_NAVY }}>
              Update Password
            </Typography>
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT, mt: 0.5 }}>
              Enter your current password and choose a new one
            </Typography>
          </Box>
        </Box>

        <Formik
          initialValues={resetPasswordValues}
          validationSchema={FormSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const response = await dispatch(updatePassword(values));
              if (response?.payload?.statusCode === 400) return;
              setShowModel(true);
            } catch (error: any) {
              console.error(error.message || "An error occurred");
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form>
              <PasswordField
                name="currentPassword"
                label={fieldLabels.currentPassword}
                showPassword={showCurrent}
                onToggle={() => setShowCurrent((v) => !v)}
              />
              <PasswordField
                name="password"
                label={fieldLabels.password}
                showPassword={showNew}
                onToggle={() => setShowNew((v) => !v)}
              />
              <PasswordField
                name="confirmPassword"
                label={fieldLabels.confirmPassword}
                showPassword={showConfirm}
                onToggle={() => setShowConfirm((v) => !v)}
              />

              <Box
                component="button"
                type="submit"
                disabled={isSubmitting}
                sx={{
                  width: "100%",
                  mt: 1,
                  py: 1.5,
                  border: "none",
                  borderRadius: "10px",
                  bgcolor: BLUE,
                  color: "#ffffff",
                  fontFamily: PROFILE_FONT,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  opacity: isSubmitting ? 0.7 : 1,
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    bgcolor: isSubmitting ? BLUE : BTN_HOVER,
                  },
                }}
              >
                Update Password
              </Box>
            </Form>
          )}
        </Formik>
      </Box>

      <AcknowledgementModel
        isVisible={showModel}
        message="Password changed successfully. You will need to re-login "
        buttonSecond="OK"
        onConfirm={onConfirm}
        onCancel={() => {}}
      />
      </Box>
    </Box>
  );
};

export default UpdatePassword;
